import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ClipboardList, Box, Truck, Users } from "lucide-react";
import { cn } from "@/utils/cn";
import { locacoesTabClass } from "./locacoesUtils";

export type CadastroSubTab = "cadastro-maquinas" | "cadastro-equipamentos" | "cadastro-operadores";

const ITEMS: Array<{ id: CadastroSubTab; label: string; icon: typeof Truck }> = [
  { id: "cadastro-operadores", label: "Operadores", icon: Users },
  { id: "cadastro-maquinas", label: "Máquinas", icon: Truck },
  { id: "cadastro-equipamentos", label: "Equipamentos", icon: Box },
];

interface Props {
  active: boolean;
  currentTab: CadastroSubTab | null;
  onSelect: (id: CadastroSubTab) => void;
}

export function CadastrosTabMenu({ active, currentTab, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
    });
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, [cancelClose]);

  const openMenu = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="menu"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          minWidth: pos.minWidth,
          zIndex: 9999,
        }}
        className="rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        {ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="menuitem"
            onClick={() => {
              onSelect(id);
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
              currentTab === id
                ? "bg-brand-50 font-medium text-brand-700"
                : "text-slate-600 hover:bg-slate-50",
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </button>
        ))}
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            updatePosition();
            openMenu();
          }
        }}
        className={locacoesTabClass(active)}
      >
        <ClipboardList className="h-4 w-4" />
        Cadastros
        <ChevronDown className={cn("h-3.5 w-3.5 opacity-60 transition-transform", open && "rotate-180")} />
      </button>
      {menu}
    </>
  );
}
